<template>
  <div class="watchers-wrap">
    <div class="watchers--collapsed">
      <div class="content-wrap">
        <div>
          <p v-if="watchersList.length" class="total-watchers message-text">
            <spinner v-if="watchersUiFlas.isFetching" size="tiny" />
            {{ totalWatchersText }}
          </p>
          <p v-else class="text-muted message-text">
            {{ $t('CONVERSATION_PARTICIPANTS.NO_PARTICIPANTS_TEXT') }}
          </p>
        </div>
        <div class="flex-end-center">
          <button
            v-if="isUserWatching"
            class="button clear button_container-align leave-button"
            @click="offSelfAssign"
          >
            <fluent-icon size="16" icon="close-icon" viewbox="0 0 16 16" />
            {{ $t('CONVERSATION_PARTICIPANTS.LEAVE_THE_CHAT') }}
          </button>
          <button
            v-else
            class="button clear button_container-align join-button"
            @click="onSelfAssign"
          >
            <fluent-icon size="16" icon="conversa2-add" viewbox="0 0 16 16" />
            {{ $t('CONVERSATION_PARTICIPANTS.WATCH_CONVERSATION') }}
          </button>
          <button
            v-tooltip.left="$t('CONVERSATION_PARTICIPANTS.ADD_PARTICIPANTS')"
            class="button smooth button_container-align"
            @click="onOpenDropdown"
          >
            <fluent-icon
              icon="add-participant"
              type="filled"
              size="16"
              viewbox="0 0 16 16"
            />
          </button>
        </div>
      </div>
    </div>
    <div class="actions">
      <thumbnail-group
        :more-thumbnails-text="moreThumbnailsText"
        :show-more-thumbnails-count="showMoreThumbs"
        :users-list="thumbnailList"
        size="32px"
        @show-all="onShowAllParticipants"
      />
    </div>
    <div
      v-on-clickaway="
        () => {
          onCloseDropdown();
        }
      "
      :class="{ 'dropdown-pane--open': showDropDown }"
      class="dropdown-pane"
    >
      <div class="dropdown__header">
        <h4 class="text-block-title text-truncate">
          {{ $t('CONVERSATION_PARTICIPANTS.ADD_PARTICIPANTS') }}
        </h4>
        <woot-button
          icon="dismiss"
          size="tiny"
          color-scheme="secondary"
          variant="clear"
          class="close-button"
          @click="onCloseDropdown"
        />
      </div>
      <multiselect-dropdown-items
        :options="agentsListParticipants"
        :selected-items="selectedWatchers"
        :has-thumbnail="true"
        :input-placeholder="placeholder"
        @click="onClickItem"
      />
    </div>

    <div v-if="contactIsGroupValidated" class="members-separator" />

    <!-- Members List Integration -->
    <div v-if="contactIsGroupValidated" class="members-list-container">
      <h4 v-if="members && members.length" class="members-list-title">
        {{ members.length }}
        {{ $t('CONVERSATION_PARTICIPANTS.MEMBERS') }}
      </h4>

      <ul class="members-list">
        <li
          v-for="member in displayedMembers"
          :key="member.id"
          class="member-list-item"
        >
          <thumbnail :username="member.name" size="32px" />
          <span class="member-name">{{ member.name }}</span>
          <span v-if="member.name !== member.phone_number" class="member-phone">
            {{ member.phone_number }}
          </span>
        </li>
        <li v-if="members.length > 10" class="member-list-item">
          <a
            v-if="!showAllMembersComputed"
            href="#"
            class="view-all-participants"
            @click.prevent="onViewAllMembersClick"
          >
            {{ $t('CONVERSATION_PARTICIPANTS.VIEW_ALL') }}
            <fluent-icon icon="chevron-right" size="20" />
          </a>
        </li>
      </ul>
    </div>
    <participants-modal
      :show="showParticipantsModal"
      :participants="selectedWatchers"
      :assigned-agent="assignedAgent"
      :on-close="onCloseParticipantsModal"
    />
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import Spinner from 'shared/components/Spinner';
import alertMixin from 'shared/mixins/alertMixin';
import { mapGetters, mapActions } from 'vuex';
import agentMixin from 'dashboard/mixins/agentMixin';
import ThumbnailGroup from 'dashboard/components/widgets/ThumbnailGroup';
import MultiselectDropdownItems from 'shared/components/ui/MultiselectDropdownItems';
import Thumbnail from 'dashboard/components/widgets/Thumbnail';
import ParticipantsModal from './ParticipantsModal.vue';

export default {
  components: {
    Spinner,
    ThumbnailGroup,
    MultiselectDropdownItems,
    Thumbnail,
    ParticipantsModal,
  },
  mixins: [alertMixin, agentMixin, clickaway],
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
    inboxId: {
      type: Number,
      default: undefined,
    },
    contactIsGroup: {
      type: Boolean,
      default: null,
    },
  },
  data() {
    return {
      selectedWatchers: [],
      showDropDown: false,
      placeholder: this.$t(
        'CONVERSATION_PARTICIPANTS.INPUT_SEARCH_PLACEHOLDER'
      ),
      showParticipantsModal: false,
    };
  },
  computed: {
    ...mapGetters({
      watchersUiFlas: 'conversationWatchers/getUIFlags',
      currentUser: 'getCurrentUser',
      members: 'getMembers',
      showAllMembers: 'getShowAllMembers',
      currentChat: 'getSelectedChat',
    }),
    // Devuelve si se deben mostrar todos los miembros del grupo.
    // Retorna false si no hay valor definido para la conversación.
    showAllMembersComputed() {
      return this.showAllMembers[this.conversationId] || false;
    },
    /**
     * Retorna todos los miembros si se indica, o solo los primeros 10.
     */
    displayedMembers() {
      const showAll = this.showAllMembersComputed;
      const allMembers = this.members || [];
      const membersArray = Array.isArray(allMembers) ? allMembers : [];

      if (showAll) {
        return membersArray;
      }
      return membersArray.slice(0, 10);
    },

    /**
     * Valida si contactIsGroup es true, considera null como false.
     */
    contactIsGroupValidated() {
      return this.contactIsGroup === true; // Si es null lo considera false
    },
    /**
     * Retorna el agente asignado a la conversación actual.
     */
    assignedAgent() {
      return this.currentChat?.meta?.assignee || null;
    },
    watchersFromStore() {
      return this.$store.getters['conversationWatchers/getByConversationId'](
        this.conversationId
      );
    },
    watchersList: {
      get() {
        return this.selectedWatchers;
      },
      set(participants) {
        this.selectedWatchers = [...participants];
        const userIds = participants.map(el => el.id);
        this.updateParticipant(userIds);
      },
    },
    isUserWatching() {
      return this.selectedWatchers.some(
        watcher => watcher.id === this.currentUser.id
      );
    },
    thumbnailList() {
      return this.selectedWatchers.slice(0, 4);
    },
    moreAgentCount() {
      const maxThumbnailCount = 4;
      return this.watchersList.length - maxThumbnailCount;
    },
    moreThumbnailsText() {
      if (this.moreAgentCount > 1) {
        return this.$t('CONVERSATION_PARTICIPANTS.REMANING_PARTICIPANTS_TEXT', {
          count: this.moreAgentCount,
        });
      }
      return this.$t('CONVERSATION_PARTICIPANTS.REMANING_PARTICIPANT_TEXT', {
        count: 1,
      });
    },
    showMoreThumbs() {
      return this.moreAgentCount > 0;
    },
    totalWatchersText() {
      if (this.selectedWatchers.length > 1) {
        return this.$t('CONVERSATION_PARTICIPANTS.TOTAL_PARTICIPANTS_TEXT', {
          count: this.selectedWatchers.length,
        });
      }
      return this.$t('CONVERSATION_PARTICIPANTS.TOTAL_PARTICIPANT_TEXT', {
        count: 1,
      });
    },
  },
  watch: {
    /**
     * Observa cambios en el ID de la conversación y actualiza participantes y miembros.
     */
    conversationId() {
      this.fetchParticipants();
    },
    watchersFromStore(participants) {
      if (!participants) {
        return;
      }

      this.selectedWatchers = [...participants];
    },
  },
  mounted() {
    this.fetchParticipants();
    this.$store.dispatch('agents/get');
  },
  methods: {
    ...mapActions(['setShowAllMembers']),
    fetchParticipants() {
      const conversationId = this.conversationId;
      this.$store.dispatch('conversationWatchers/show', { conversationId });
    },
    /** Muestra el modal con todos los participantes. */
    onShowAllParticipants() {
      this.showParticipantsModal = true;
    },

    /** Cierra el modal de participantes. */
    onCloseParticipantsModal() {
      this.showParticipantsModal = false;
    },
    async updateParticipant(userIds) {
      const conversationId = this.conversationId;
      let alertMessage = this.$t(
        'CONVERSATION_PARTICIPANTS.API.SUCCESS_MESSAGE'
      );

      try {
        await this.$store.dispatch('conversationWatchers/update', {
          conversationId,
          userIds,
        });
      } catch (error) {
        alertMessage =
          error?.message ||
          this.$t('CONVERSATION_PARTICIPANTS.API.ERROR_MESSAGE');
      } finally {
        this.showAlert(alertMessage);
      }
      this.fetchParticipants();
    },
    onOpenDropdown() {
      this.showDropDown = true;
    },
    onCloseDropdown() {
      this.showDropDown = false;
    },
    onClickItem(agent) {
      const isAgentSelected = this.watchersList.some(
        participant => participant.id === agent.id
      );

      if (isAgentSelected) {
        const updatedList = this.watchersList.filter(
          participant => participant.id !== agent.id
        );

        this.watchersList = [...updatedList];
      } else {
        this.watchersList = [...this.watchersList, agent];
      }
    },
    /**
     * Asigna al usuario actual como participante de la conversación.
     */
    onSelfAssign() {
      this.watchersList = [...this.selectedWatchers, this.currentUser];
    },
    /**
     * Elimina al usuario actual de la lista de participantes.
     */
    offSelfAssign() {
      this.watchersList = this.selectedWatchers.filter(
        participant => participant.id !== this.currentUser.id
      );
    },
    /**
     * Genera las iniciales a partir de un nombre completo.
     */
    getInitials(name) {
      if (!name) return '';
      const nameParts = name.split(' ');
      let initials = '';
      for (let i = 0; i < nameParts.length; i++) {
        initials += nameParts[i].charAt(0).toUpperCase();
      }
      return initials;
    },
    // Maneja el clic para ver todos los miembros del grupo.
    // Actualiza el estado para mostrar todos los miembros.
    onViewAllMembersClick() {
      this.setShowAllMembers({
        conversationId: this.conversationId,
        value: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.watchers-wrap {
  position: relative;
}

.add-participant {
  color: var(--conversa2-blue-700-color);
}

.content-wrap {
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: var(--space-smaller);
  align-items: center;
}

.watchers--collapsed {
  display: flex;
  justify-content: space-between;
}

.dropdown-pane {
  box-sizing: border-box;
  top: var(--space-large);
  width: 100%;
  padding: var(--space-small) 0;
  border-radius: var(--border-radius-normal-plus);
  border: 1px solid var(--conversa2-neutral-100-color);
  background: white;

  ::v-deep .search-wrap {
    padding: 0 var(--space-slab);
    margin-bottom: var(--space-slab);

    .search-input {
      background: var(--conversa2-neutral-50-color);

      &::placeholder {
        color: var(--conversa2-neutral-800-color);
        opacity: 0.5;
      }
    }
  }

  ::v-deep .list-scroll-container {
    padding: 0 var(--space-small);
  }

  ::v-deep .multiselect-dropdown--item {
    padding: var(--space-small);

    &.active {
      background: var(--conversa2-neutral-100-color);
      border-color: var(--conversa2-neutral-100-color);
    }

    .user-wrap {
      gap: var(--space-slab);
    }

    .user-thumbnail-box {
      width: 32px !important;
      height: 32px !important;

      .avatar-container {
        font-size: var(--font-size-small) !important;
        line-height: 20px !important;
      }
    }

    .name {
      color: var(--conversa2-neutral-800-color);
      font-size: var(--font-size-default);
      line-height: 24px;
      margin: 0;
    }
  }
}
.dropdown__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-smaller);
  padding: 0 var(--space-slab);

  .text-block-title {
    margin: 0;
    font-size: var(--font-size-default);
    line-height: 24px;
  }

  .close-button ::v-deep svg {
    width: 16px;
    height: 16px;
  }
}

.actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.text-muted,
.total-watchers {
  color: var(--conversa2-neutral-800-color);
  font-weight: 600;
  line-height: 2rem;
  opacity: 1;
}

.message-text {
  margin: 0;
  font-size: var(--font-size-small);
}

.members-list-container {
  margin-bottom: var(--space-small);
  border-bottom-width: 1px;
  padding-left: 0;
}

.members-list-title {
  margin-bottom: var(--space-smaller);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-small);
  line-height: var(--space-two);
  letter-spacing: 0px;
  vertical-align: middle;
  color: var(--conversa2-neutral-800-color);
}

.members-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.member-list-item {
  display: flex;
  align-items: center;
  padding: var(--space-smaller) 0;
  height: 56px;
  padding-top: 12px;
  padding-bottom: 12px;
  justify-content: space-between;

  &:last-child {
    border-bottom: none;
  }
}

.member-name {
  flex-grow: 1;
  text-align: left;
  margin-left: 12px;
  font-weight: var(--font-weight-normal);
  font-size: var(--font-size-default);
  line-height: var(--space-medium);
  letter-spacing: 0px;
  vertical-align: middle;
}

.member-phone {
  text-align: right;
  font-weight: var(--font-weight-normal);
  font-size: var(--font-size-small);
  line-height: var(--space-two);
  letter-spacing: 0px;
  vertical-align: middle;
  color: var(--conversa2-neutral-600-color);
}

.view-all-participants {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--font-size-small);
  margin-top: var(--space-smaller);
  text-decoration: none;
  box-sizing: border-box;
  font-weight: var(--font-weight-normal);
  line-height: var(--space-two);
  letter-spacing: 0px;
  vertical-align: middle;
  width: 100%;
  padding-top: var(--space-normal);
  padding-right: var(--space-small);
  padding-bottom: var(--space-normal);
  padding-left: var(--space-small);
  color: var(--conversa2-blue-700-color);
}

.members-separator {
  height: 1px;
  background-color: var(--conversa2-neutral-100-color);
  width: calc(100% + 32px);
  margin-left: -16px;
  margin-top: var(--space-normal);
  margin-bottom: var(--space-normal);
}

.join-button,
.leave-button {
  height: 3.2rem;
  padding: 0.8rem 1.6rem;
}

.flex-end-center {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
}
</style>
